import { put } from 'redux-saga/effects';
import {
  UPDATE_PERSISTED_COMMON_STATE,
  UPDATE_PERSISTED_COMMON_STATE_FOR_PRESET,
} from '@store/common/types';
import { UPDATE_PERSISTED_NOC_STATE } from '@store/noc/base/types';
import { UPDATE_PERSISTED_OVERVIEW_STATE } from '@store/overviewDashboard/base/types';
import { UPDATE_PERSISTED_ML_STATE } from '@store/metricLens/base/types';
import { UPDATE_PERSISTED_VM_STATE } from '@store/viewerModule/types';
import { urlTransform } from './urlTransform';
import { getValidPresetKeys } from '@utils/presets/utils';
import {
  APP_FLOW_PRESET_ROUTE_PATH_REGEX,
  APP_FRUSTRATION_SIGNAL_PRESET_ROUTE_PATH_REGEX,
  APP_EVENT_PRESET_ROUTE_PATH_REGEX,
} from './preset';

const { baseUrl } = window.conviva;

export const APP_FLOW_PERSISTENCE_KEY_REGEX = /\/preset-flow:(\d+)$/;
export const APP_FRUSTRATION_SIGNAL_PERSISTENCE_KEY_REGEX = /\/preset-frustration-signal:([\w-]+)$/;
export const APP_EVENT_PERSISTENCE_KEY_REGEX = /\/preset-event:(.+)$/;

export const trendsRouteKey = '/';
export const nocRouteKey = '/noc';
export const ovbRouteKey = '/ovb';
export const mlRouteKey = '/metriclens';
export const aeRouteKey = '/app-experience';
export const vmRouteKey = '/vm';

export const getPresetKey = (persistenceKey: string) => {
  const matchAppFlowPreset = persistenceKey.match(APP_FLOW_PERSISTENCE_KEY_REGEX);

  if (matchAppFlowPreset) {
    return matchAppFlowPreset[1];
  }

  const matchFrustrationSignalPreset = persistenceKey.match(
    APP_FRUSTRATION_SIGNAL_PERSISTENCE_KEY_REGEX,
  );

  if (matchFrustrationSignalPreset) {
    return matchFrustrationSignalPreset[1];
  }

  const matchEventPreset = persistenceKey.match(APP_EVENT_PERSISTENCE_KEY_REGEX);

  if (matchEventPreset) {
    return matchEventPreset[1];
  }

  return persistenceKey.slice(8);
};

export const checkValidPresetPersistenceKey = (key: string) =>
  APP_FLOW_PERSISTENCE_KEY_REGEX.test(key) ||
  APP_FRUSTRATION_SIGNAL_PERSISTENCE_KEY_REGEX.test(key) ||
  APP_EVENT_PERSISTENCE_KEY_REGEX.test(key) ||
  getValidPresetKeys()
    .map((item) => `/preset-${item}`)
    .includes(key);

export const checkValidPersistenceKey = (key: string) =>
  Object.values(getRoutesWithPersistence()).includes(key) ||
  APP_FLOW_PERSISTENCE_KEY_REGEX.test(key) ||
  APP_FRUSTRATION_SIGNAL_PERSISTENCE_KEY_REGEX.test(key) ||
  APP_EVENT_PERSISTENCE_KEY_REGEX.test(key);

const getPresetUseCaseRoutesAndKeys = () => {
  const presetRoutes = {};
  const presetKeyEventMap = {};
  const presetKeys = [] as string[];
  const validPresetKeys = Object.values(getValidPresetKeys());

  validPresetKeys.forEach((presetKey) => {
    const persistenceKey = `/preset-${presetKey}`;
    presetRoutes[`${baseUrl}/dashboard/preset/${presetKey}`] = persistenceKey;
    presetKeyEventMap[persistenceKey] = UPDATE_PERSISTED_COMMON_STATE_FOR_PRESET;
    presetKeys.push(persistenceKey);
  });
  return { presetRoutes, presetKeyEventMap, presetKeys };
};

export const getRoutesWithPersistence = () => ({
  '/': trendsRouteKey,
  [`${baseUrl}/dashboard/trends`]: trendsRouteKey,
  [`${baseUrl}/dashboard/real-time`]: nocRouteKey,
  [`${baseUrl}/dashboard/overview`]: ovbRouteKey,
  [`${baseUrl}/dashboard/metriclens`]: mlRouteKey,
  [`${baseUrl}/dashboard/app-experience`]: aeRouteKey,
  [`${baseUrl}/dashboard/viewer-module`]: vmRouteKey,
  ...getPresetUseCaseRoutesAndKeys().presetRoutes,
});

export const getKeyToEventMap = (key: string) => {
  if (APP_FLOW_PERSISTENCE_KEY_REGEX.test(key)) return UPDATE_PERSISTED_COMMON_STATE_FOR_PRESET;
  if (APP_FRUSTRATION_SIGNAL_PERSISTENCE_KEY_REGEX.test(key))
    return UPDATE_PERSISTED_COMMON_STATE_FOR_PRESET;
  if (APP_EVENT_PERSISTENCE_KEY_REGEX.test(key)) return UPDATE_PERSISTED_COMMON_STATE_FOR_PRESET;

  const predefinedMap = {
    [trendsRouteKey]: UPDATE_PERSISTED_COMMON_STATE,
    [nocRouteKey]: UPDATE_PERSISTED_NOC_STATE,
    [ovbRouteKey]: UPDATE_PERSISTED_OVERVIEW_STATE,
    [mlRouteKey]: UPDATE_PERSISTED_ML_STATE,
    [aeRouteKey]: UPDATE_PERSISTED_COMMON_STATE,
    [vmRouteKey]: UPDATE_PERSISTED_VM_STATE,
    ...getPresetUseCaseRoutesAndKeys().presetKeyEventMap,
  };

  return predefinedMap[key] ?? 'NO_PERSISTANCE';
};

export const getPersistenceKeys = () => [
  trendsRouteKey,
  nocRouteKey,
  ovbRouteKey,
  mlRouteKey,
  aeRouteKey,
  vmRouteKey,
  ...getPresetUseCaseRoutesAndKeys().presetKeys,
];

/**
 * For implementing this persistence feature, we will ensure which page to store the configuration.
 * This function can return the hash value of current url, except some special situations
 * @returns {*|string}
 */
export const getPersistentHashKey = () => {
  const currentUrl = window.location.pathname;

  // Check app flow preset, key should be `/preset-flow:<id>`
  const matchAppFlowPreset = currentUrl.match(APP_FLOW_PRESET_ROUTE_PATH_REGEX);
  if (matchAppFlowPreset) {
    return `/preset-flow:${matchAppFlowPreset[1]}`;
  }

  // Check frustration signal preset, key should be `/preset-frustration-signal:<id>`
  const matchFrustrationSignalPreset = currentUrl.match(
    APP_FRUSTRATION_SIGNAL_PRESET_ROUTE_PATH_REGEX,
  );
  if (matchFrustrationSignalPreset) {
    return `/preset-frustration-signal:${matchFrustrationSignalPreset[1]}`;
  }

  // Check event preset, key should be `/preset-event:<id>`
  const matchEventPreset = currentUrl.match(APP_EVENT_PRESET_ROUTE_PATH_REGEX);
  if (matchEventPreset) {
    return `/preset-event:${matchEventPreset[1]}`;
  }

  const transformUrl = urlTransform(currentUrl);
  const routesWithPersistence = getRoutesWithPersistence();

  return routesWithPersistence[transformUrl] || transformUrl;
};

/**
 * Validates and extracts configuration from persistence data
 * @param persistence - The persistence data object from Redux store
 * @param key - The persistence key to look up
 * @returns Object with validation result: { valid: boolean, configuration?: any, error?: string }
 */
export const validateAndExtractConfiguration = (persistence: any, key: string) => {
  if (!key) {
    return { valid: false, error: 'Unable to determine persistence key' };
  }

  if (!persistence) {
    return {
      valid: false,
      error: `No persistence data found for key: ${key}. Please save your configuration first.`,
    };
  }

  const persistenceData = persistence[key];
  if (!persistenceData) {
    return {
      valid: false,
      error: `No persistence data found for key: ${key}. Please save your configuration first.`,
    };
  }

  const response = persistenceData.response;
  if (!response) {
    return {
      valid: false,
      error: 'Persistence response not found. Please wait for persistence to load.',
    };
  }

  const configuration = response.configuration;
  if (!configuration) {
    return {
      valid: false,
      error:
        'Configuration not found in persistence response. Please save your current configuration first.',
    };
  }

  if (configuration === null || configuration === undefined) {
    return {
      valid: false,
      error: 'Configuration is null or undefined. Please save your current configuration first.',
    };
  }

  if (typeof configuration === 'object' && Object.keys(configuration).length === 0) {
    return {
      valid: false,
      error: 'Configuration is empty. Please save your current configuration first.',
    };
  }

  return { valid: true, configuration };
};

// adds a skip persistence flag to the action
// the persistence update config will ignore the corresponding action
const skipPersistence = (action) => ({ ...action, skipPersistence: true });
export function* setPersistedData(action) {
  yield put(skipPersistence(action));
}
